import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { RefObject, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { ApiResponse } from '@cineamo/api/types/ApiResponse.types';

type UsePaginationOptions<T extends Record<string, unknown>> = {
    initialData?: InfiniteData<ApiResponse<unknown, T>>;
    initialDataUpdatedAt?: number;
    elementIdOrClass?: string;
    enabled?: boolean;
    keepPreviousData?: boolean;
    root?: RefObject<Element>;
    rootMargin?: string;
};

type UsePaginationReturnType<T extends Record<string, unknown>> = {
    data: InfiniteData<ApiResponse<unknown, T>>;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    hasNextPage: boolean | null;
    hasPreviousPage: boolean;
    remainingItemCount?: number | null;
    ref: (node?: Element) => void;
    fetchNextPage: (
        options?: never //: FetchNextPageOptions
    ) => Promise</*InfiniteQueryObserverResult<ApiResponse<unknown, T>, unknown>*/ unknown>;
    refetch: () => Promise<unknown>;
    isLoading: boolean;
};

export function usePagination<T extends Record<string, unknown>>(
    dependencies: (string | unknown)[],
    apiFunction: (pageParam?: number) => Promise<ApiResponse<unknown, T> | undefined | null>,
    options?: UsePaginationOptions<T>
): UsePaginationReturnType<T> {
    // The root ref's element is not mounted yet on the first render; mirror it into state so
    // the IntersectionObserver is recreated once the scroll container exists.
    const [rootElement, setRootElement] = useState<Element | null>(null);
    useEffect(() => {
        setRootElement(options?.root?.current ?? null);
    });

    const { ref, inView, entry } = useInView({
        root: rootElement ?? undefined,
        rootMargin: options?.rootMargin
    });

    useEffect(() => {
        if (!entry?.target && options?.elementIdOrClass) {
            ref(
                document.getElementById(options.elementIdOrClass) ??
                    document.getElementsByClassName(options.elementIdOrClass)?.[0]
            );
        }
    });

    const { data, isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, hasPreviousPage, refetch } =
        useInfiniteQuery({
            queryKey: dependencies,
            queryFn: async ({ pageParam }) => {
                return (await apiFunction((pageParam as unknown as number) ?? 1)) ?? ({} as ApiResponse<unknown, T>);
            },
            getPreviousPageParam: (firstPage: ApiResponse<unknown, T>) => {
                return firstPage && firstPage._page && firstPage._page > 0 ? firstPage._page - 1 : null;
            },
            getNextPageParam: (lastPage: ApiResponse<unknown, T>) => {
                return lastPage && (lastPage._page ?? 0) < (lastPage._page_count ?? 0)
                    ? (lastPage._page ?? 0) + 1
                    : null;
            },
            staleTime: 60000,
            refetchOnWindowFocus: false,
            initialPageParam: 1,
            ...(options?.keepPreviousData !== undefined && { keepPreviousData: options.keepPreviousData }),
            ...(options?.initialDataUpdatedAt ? { initialDataUpdatedAt: options.initialDataUpdatedAt } : {}),
            initialData: () => {
                if (options?.initialData) {
                    return {
                        pageParams: options.initialData.pageParams,
                        pages: options.initialData.pages
                    };
                }
            }
        });

    useEffect(
        () => {
            // `inView` can be stale: the observer only fires on intersection *changes*, but this effect
            // also re-runs when a fetch completes or the page re-renders. With a bogus initial reading
            // (e.g. pre-layout during hydration) that would cascade page after page. When a scroll root
            // is configured we therefore re-measure the sentinel against the lookahead window live.
            function isSentinelWithinLookahead(): boolean {
                const rootNode = options?.root?.current;
                const target = entry?.target;
                if (!rootNode || !target) {
                    return true;
                }
                const rootRect = rootNode.getBoundingClientRect();
                const targetRect = target.getBoundingClientRect();
                const parts = (options?.rootMargin ?? '0px').trim().split(/\s+/);
                // CSS shorthand: 1 → all; 2 → top/bottom right/left; 3 → top right/left bottom; 4 → top right bottom left
                const [top, right, bottom, left] =
                    parts.length === 1
                        ? [parts[0], parts[0], parts[0], parts[0]]
                        : parts.length === 2
                          ? [parts[0], parts[1], parts[0], parts[1]]
                          : parts.length === 3
                            ? [parts[0], parts[1], parts[2], parts[1]]
                            : [parts[0], parts[1], parts[2], parts[3]];
                const resolveMargin = (value: string | undefined, base: number): number => {
                    if (!value) {
                        return 0;
                    }
                    return value.endsWith('%') ? (parseFloat(value) / 100) * base : parseFloat(value) || 0;
                };
                const marginTop = resolveMargin(top, rootRect.height);
                const marginRight = resolveMargin(right, rootRect.width);
                const marginBottom = resolveMargin(bottom, rootRect.height);
                const marginLeft = resolveMargin(left, rootRect.width);
                return (
                    targetRect.left <= rootRect.right + marginRight &&
                    targetRect.right >= rootRect.left - marginLeft &&
                    targetRect.top <= rootRect.bottom + marginBottom &&
                    targetRect.bottom >= rootRect.top - marginTop
                );
            }

            if (inView && hasNextPage && !isFetchingNextPage && isSentinelWithinLookahead()) {
                // cancelRefetch: false — the default would abort and restart an in-flight
                // next-page fetch every time this effect re-runs, losing pages under fast scrolling.
                fetchNextPage({ cancelRefetch: false }).then();
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inView, hasNextPage, isFetchingNextPage]
    );

    const pages = data?.pages?.filter((page) => page) ?? [];
    const totalItems = data?.pages?.[0]?._total_items ?? null;
    const currentItemCount = pages?.reduce((count, page) => count + findItems(page).length, 0) ?? null;
    function findItems(page: ApiResponse<unknown, T>): unknown[] {
        if (!page?._embedded) {
            return [];
        }
        return Object.values(page._embedded).find((obj) => obj instanceof Array) as unknown[];
    }

    const remainingItemCount = totalItems && pages ? Math.max(0, totalItems - currentItemCount) : null;

    return {
        ref: ref,
        data: {
            pages: pages,
            pageParams: data?.pageParams?.filter((pageParam) => pageParam) ?? []
        },
        isFetching: isFetching,
        isFetchingNextPage: isFetchingNextPage,
        hasNextPage: isFetching ? null : hasNextPage,
        hasPreviousPage: hasPreviousPage,
        fetchNextPage: fetchNextPage,
        remainingItemCount: remainingItemCount,
        refetch: refetch,
        isLoading: isFetching && pages.length <= 0
    };
}
